import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';

import { cssVar } from '@feathr/hooks';

import {
  formatAxisDate,
  formatCountTooltipValue,
  type IDonationCountChartRow,
} from './DonationFormTrends.utils';
import type { ITrendChartLegendEntry } from './TrendChartFrame';
import TrendChartFrame from './TrendChartFrame';

export interface IDonationCountTrendChartProps {
  /** Chart-ready rows produced by `buildDonationCountChartData`. */
  data: IDonationCountChartRow[];
}

/**
 * Donation Count Trend — recharts LineChart overlaying current vs previous
 * period donation counts. Current is solid sky-600, previous is dashed
 * neutral-500 per Figma node 10162:10992. The current line is rendered last
 * so it wins z-order at intersections.
 */
function DonationCountTrendChart({ data }: Readonly<IDonationCountTrendChartProps>): JSX.Element {
  const { t } = useTranslation();
  const currentColor = cssVar('--color-sky-600');
  const previousColor = cssVar('--color-neutral-500');

  const legend: ITrendChartLegendEntry[] = [
    { label: t('Current Period'), color: currentColor },
    { label: t('Previous Period'), color: previousColor, dashed: true },
  ];

  return (
    <TrendChartFrame
      dataName={'donation-count-trend-chart'}
      legend={legend}
      legendDataName={'donation-count-trend-legend'}
      subtitle={t('Number of donations over time')}
      title={t('Donation Count Trend')}
    >
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid
          stroke={cssVar('--color-decorative-secondary')}
          strokeDasharray={'4 4'}
          vertical={false}
        />
        <XAxis
          axisLine={false}
          dataKey={'date'}
          interval={'preserveStartEnd'}
          minTickGap={24}
          tickFormatter={formatAxisDate}
          tickLine={false}
        />
        <YAxis axisLine={false} tickLine={false} width={48} />
        <Tooltip formatter={formatCountTooltipValue} labelFormatter={formatAxisDate} />
        <Line
          dataKey={'countPrevious'}
          dot={false}
          name={t('Donations (Previous)')}
          stroke={previousColor}
          strokeDasharray={'4 4'}
          strokeWidth={2}
          type={'monotone'}
        />
        <Line
          dataKey={'countCurrent'}
          dot={false}
          name={t('Donations (Current)')}
          stroke={currentColor}
          strokeWidth={2}
          type={'monotone'}
        />
      </LineChart>
    </TrendChartFrame>
  );
}

export default DonationCountTrendChart;
